import type { InterpolatedDrone } from '../networking/Interpolator';
import type { Vec3 } from '../../shared/utils';

interface TrackedDrone {
  id: string;
  ownerId: string;
  droneType: number;
  tier: number;
  health: number;
  maxHealth: number;
  alive: boolean;
  position: Vec3;
  abilityCooldown: number;
  activeCardSlot: number;
  activeCardType: number;
  shielded: boolean;
  taunted: boolean;
  slowed: boolean;
  invulnerableMs: number;
  knockbackUntilMs: number;
  stunnedRemainingMs: number;
  dotUntilMs: number;
  dotDps: number;
}

const DEFAULT_CENTER: Vec3 = { x: 0, y: 20, z: 0 };

export class DroneStateManager {
  private ownDrones: TrackedDrone[] = [];
  private enemyDrones: TrackedDrone[] = [];
  /**
   * Per-frame id index covering BOTH `ownDrones` and `enemyDrones`. Lets
   * DamageDealt audio dispatch resolve `attackerId → DroneType` in O(1)
   * instead of doing a forEach scan over every player × every drone for
   * each combat event. Cleared and rebuilt each `updateDrones()` call.
   */
  private droneById: Map<string, TrackedDrone> = new Map();
  private swarmCenter: Vec3 = { ...DEFAULT_CENTER };

  updateDrones(drones: InterpolatedDrone[], localPlayerId: string): void {
    this.ownDrones = [];
    this.enemyDrones = [];
    this.droneById.clear();

    for (const d of drones) {
      if (!d.alive) continue;

      const tracked: TrackedDrone = {
        id: d.id,
        ownerId: d.ownerId,
        droneType: d.droneType,
        tier: d.tier,
        health: d.health,
        maxHealth: d.maxHealth,
        alive: d.alive,
        position: d.position,
        abilityCooldown: d.abilityCooldown,
        activeCardSlot: d.activeCardSlot,
        activeCardType: d.activeCardType,
        shielded: d.shielded,
        taunted: d.taunted,
        slowed: d.slowed,
        invulnerableMs: d.invulnerableMs,
        knockbackUntilMs: d.knockbackUntilMs,
        stunnedRemainingMs: d.stunnedRemainingMs,
        dotUntilMs: d.dotUntilMs,
        dotDps: d.dotDps,
      };

      if (d.ownerId === localPlayerId) {
        this.ownDrones.push(tracked);
      } else {
        this.enemyDrones.push(tracked);
      }
      this.droneById.set(d.id, tracked);
    }

    this.swarmCenter = this.computeSwarmCenter();
  }

  /**
   * O(1) lookup of `droneType` by id across both own and enemy drones.
   * Returns undefined for unknown ids — caller should fall back to a
   * generic effect (environmental damage, dead-but-not-removed actors,
   * stale event after state churn).
   */
  getDroneTypeById(droneId: string): number | undefined {
    return this.droneById.get(droneId)?.droneType;
  }

  getOwnDrones(): TrackedDrone[] {
    return this.ownDrones;
  }

  getEnemyDrones(): TrackedDrone[] {
    return this.enemyDrones;
  }

  getSwarmCenter(): Vec3 {
    return this.swarmCenter;
  }

  getAliveDroneCount(): number {
    return this.ownDrones.length;
  }

  private computeSwarmCenter(): Vec3 {
    if (this.ownDrones.length === 0) return { ...DEFAULT_CENTER };

    let cx = 0;
    let cy = 0;
    let cz = 0;
    for (const d of this.ownDrones) {
      cx += d.position.x;
      cy += d.position.y;
      cz += d.position.z;
    }
    const n = this.ownDrones.length;
    return { x: cx / n, y: cy / n, z: cz / n };
  }
}
